import { type PdfPageBaseLayout, Modal, ModalProps } from "@skailer-com/uikit";
import { ESIGN_PAGE_WIDTH } from "@/pages/esign/esign.config.ts";
import { EsignFilePreview } from "@/pages/esign/components/EsignFilePreview.tsx";
import { EsignPreviewFieldsLayer } from "@/pages/esign/components/EsignPreviewFieldsLayer.tsx";
import { useEsignMarkup } from "@/queries/use-esign.query.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiDocumentPreview } from "@/types/document.types.ts";
import { ApiEsignMarkupField, ApiEsignMarkupRole } from "@/types/esign.types.ts";
import { useMemo } from "react";

interface EsignPreviewFieldEntry {
  role: ApiEsignMarkupRole;
  roleIndex: number;
  field: ApiEsignMarkupField;
}

export interface EsignDocumentPreviewModalProps extends ModalProps {
  documentId: number;
  file: ApiFile;
  fileName: string;
  preview: ApiDocumentPreview;
  showFields?: boolean;
}

export function EsignDocumentPreviewModal({
  documentId,
  file,
  fileName,
  preview,
  showFields = true,
  ...props
}: EsignDocumentPreviewModalProps) {
  const { data: markup } = useEsignMarkup(documentId, showFields);

  const pageMarkerRoleIndexes = useMemo(() => {
    const pageRoles: Record<number, number[]> = {};

    markup?.roles.forEach((role, roleIndex) => {
      role.fields.forEach((field) => {
        const pageNumber = field.page + 1;
        const roleIndexes = pageRoles[pageNumber] ?? [];
        if (!roleIndexes.includes(roleIndex)) roleIndexes.push(roleIndex);
        pageRoles[pageNumber] = roleIndexes;
      });
    });

    return pageRoles;
  }, [markup]);

  const renderPageOverlay = (pageNumber: number, baseLayout: PdfPageBaseLayout) => {
    const entries: EsignPreviewFieldEntry[] = [];

    markup?.roles.forEach((role, roleIndex) => {
      role.fields.forEach((field) => {
        if (field.page === pageNumber - 1) entries.push({ role, roleIndex, field });
      });
    });

    if (entries.length === 0) return null;

    return <EsignPreviewFieldsLayer baseLayout={baseLayout} entries={entries} />;
  };

  return (
    <Modal
      {...props}
      title={fileName}
      withCloser={true}
      layout="stretch"
      variant="middle"
      size="full"
    >
      <EsignFilePreview
        file={file}
        preview={preview}
        documentId={documentId}
        maxRenderWidth={ESIGN_PAGE_WIDTH}
        pageNavigationPlacement="inside"
        pageMarkerRoleIndexes={pageMarkerRoleIndexes}
        renderPageOverlay={renderPageOverlay}
      />
    </Modal>
  );
}
